import {
    Badge,
    Box,
    Button,
    Group,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import { SearchableSelect, SearchableSelectCreatable } from '../../../common';
import {
    getChatBots,
    getChatScripts,
    getUserVerticals,
    getUsers,
    getVerticals,
} from '../../../../http/apiClient';
import { useEffect, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OFFER_CATEGORIES } from '../../../common/enums/enums';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { genericValidator } from '../../../../utils/validation';
import { hasRole } from '../../../../utils/roles';
import useAddChatHandler from '../../../../http/mutations/campaigns/useAddChatHandler';
import { useAuthStore } from '../../../../store';
import { useForm } from '@mantine/form';

const CreateChatHandlerModal = ({ existing, opened, close }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const form = useForm({
        initialValues: {},
        validateInputOnBlur: true,
        validate: {
            name: (value) => {
                const { type, message } = genericValidator(
                    'Handler name',
                    value,
                    {
                        required: true,
                        minLength: 6,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    }
                );
                if (type) {
                    return type === 'pattern'
                        ? 'Handler name must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },
            userId: (value) => {
                const { message } = genericValidator('User ID', value, {
                    required: hasRole(userProfile, 'SUPER_ADMIN'),
                });
                return message;
            },
            scriptId: (value, values) => {
                if (values.type != 'Script') {
                    return;
                }

                const { message } = genericValidator('Script', value, {
                    required: true,
                });
                return message;
            },
            chatBotId: (value, values) => {
                if (values.type != 'ChatBot') {
                    return;
                }

                const { message } = genericValidator('Chat bot', value, {
                    required: true,
                });
                return message;
            },
        },
    });

    useEffect(() => {
        let type = 'Script';
        if (existing?.script) {
            type = 'Script';
        } else if (existing?.chatBot) {
            type = 'ChatBot';
        }

        const initialValues = {
            id: existing?.id.toString(),
            name: existing?.name || '',
            userId: hasRole(userProfile, 'SUPER_ADMIN')
                ? userProfile?.id.toString()
                : null,
            type: type,
            scriptId: existing?.script?.value,
            chatBotId: existing?.chatBot?.value,
        };

        form.setInitialValues(initialValues);
        form.reset();
    }, [existing?.id, opened]);

    const HandlerTypeSubview = useMemo(() => {
        switch (form.values.type) {
            case 'Script':
                return ScriptSubview;

            case 'ChatBot':
                return BotSubview;

            default:
                return;
        }
    }, [form.values?.type]);

    const { isPending, mutate: addOrUpdate } = useAddChatHandler();

    const onSubmit = (model) => {
        const newModel = {
            ...model,
        };
        if (newModel.type === 'Script') {
            delete newModel.chatBotId;
        } else if (newModel.type === 'ChatBot') {
            delete newModel.scriptId;
        }

        addOrUpdate(newModel, {
            onSuccess(data, variables, context) {
                close();
            },
            onError(error, variables, context) {
                // setError(error);
            },
        });
    };

    return (
        <Modal.Root
            opened={opened}
            onClose={close}
            size={'md'}>
            <Modal.Overlay />
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>
                        {existing ? 'Update' : 'Create'} Chat Handler
                    </Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={form.onSubmit(onSubmit)}>
                        <Stack gap={'xs'}>
                            <TextInput
                                label={'Handler Name'}
                                placeholder='Enter a name'
                                withAsterisk
                                key={form.key('name')}
                                {...form.getInputProps('name')}
                            />

                            {hasRole(userProfile, 'SUPER_ADMIN') ? (
                                <SearchableSelect
                                    label={'User'}
                                    placeholder='User'
                                    queryHttpFn={getUsers}
                                    queryKey={'users'}
                                    optionsMapFn={(user) => ({
                                        label: user.username,
                                        value: user.id.toString(),
                                    })}
                                    withAsterisk
                                    key={form.key('userId')}
                                    {...form.getInputProps('userId')}
                                />
                            ) : null}

                            <Select
                                label={'Type'}
                                placeholder='Select a group type'
                                data={['Script', 'ChatBot']}
                                withAsterisk
                                key={form.key('type')}
                                {...form.getInputProps('type')}
                            />

                            {form.values.type ? (
                                <HandlerTypeSubview
                                    existing={existing}
                                    form={form}
                                />
                            ) : null}

                            <Group justify='flex-end'>
                                <Button
                                    type={'submit'}
                                    disabled={isPending || !form.isValid()}
                                    rightSection={
                                        isPending ? (
                                            <FontAwesomeIcon
                                                icon={faSpinnerThird}
                                                spin
                                            />
                                        ) : null
                                    }>
                                    {existing ? 'Update' : 'Create'}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};

const ScriptSubview = ({ existing, form }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const userQueryHttpFn = (queryHttpFn, queryParams) => {
        const userId = form.values.userId;
        const searchUserRoutes = hasRole(userProfile, 'SUPER_ADMIN') && userId;

        if (searchUserRoutes) {
            return queryHttpFn({
                userId,
                pagination: queryParams,
            });
        }
        return queryHttpFn({
            pagination: queryParams,
        });
    };

    return (
        <SearchableSelect
            label={'Chat Script'}
            withAsterisk
            placeholder='Select chat script'
            queryHttpFn={(queryParams) =>
                userQueryHttpFn(getChatScripts, queryParams)
            }
            queryKey={
                form.values.userId
                    ? ['chat_scripts', form.values.userId]
                    : ['chat_scripts']
            }
            optionsMapFn={(route) => ({
                label: route.name,
                value: route.id.toString(),
            })}
            defaultValue={existing?.script}
            key={form.key('scriptId')}
            {...form.getInputProps('scriptId')}
        />
    );
};

const BotSubview = ({ existing, form }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const userQueryHttpFn = (queryHttpFn, queryParams) => {
        const userId = form.values.userId;
        const searchUserRoutes = hasRole(userProfile, 'SUPER_ADMIN') && userId;

        if (searchUserRoutes) {
            return queryHttpFn({
                userId,
                pagination: queryParams,
            });
        }
        return queryHttpFn({
            pagination: queryParams,
        });
    };

    return (
        <SearchableSelect
            label={'Chat Bot'}
            withAsterisk
            placeholder='Select chat bot'
            queryHttpFn={(queryParams) =>
                userQueryHttpFn(getChatBots, queryParams)
            }
            queryKey={
                form.values.userId
                    ? ['chat_bots', form.values.userId]
                    : ['chat_bots']
            }
            optionsMapFn={(route) => ({
                label: route.name,
                value: route.id.toString(),
            })}
            defaultValue={existing?.chatBot}
            key={form.key('chatBotId')}
            {...form.getInputProps('chatBotId')}
        />
    );
};

export default CreateChatHandlerModal;
